import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useLeadDetail, useLeadMutations, useActivityMutations } from '../hooks/useCRM';
import {
    recomputeLeadMatches,
    getLeadMatches,
    queryLeadMatches,
    getLeadDeals,
    createLeadDeal,
    linkLeadDeal,
    unlinkLeadDeal,
    updateDeal,
    getDeals
} from '../utils/crmApi';
import ActivityTimeline from '../components/ActivityTimeline';
import LeadForm from '../components/LeadForm';
import LeadRequirementsForm from '../components/LeadRequirementsForm';
import PropertyMatchCard from '../components/PropertyMatchCard';
import FilesPanel from '../components/FilesPanel';
import { useToast } from '../components/Toast';
import { useConfirm } from '../hooks/useConfirm';
import ConfirmModal from '../../../components/ConfirmModal';
import { getStoredAuthUser, resolveLeadOwnerName } from '../utils/leadOwner';
import '../crm.css';
import { contactsPath, dealsPath, leadsPath, dealPath } from '../utils/crmEnv';

const statusLabels = {
    new: 'New',
    contacted: 'Contacted',
    qualified: 'Qualified',
    unqualified: 'Unqualified',
    converted: 'Converted',
};

const sourceLabels = {
    website: 'Website',
    referral: 'Referral',
    cold_call: 'Cold Call',
    advertisement: 'Advertisement',
    social_media: 'Social Media',
    walk_in: 'Walk-in',
    other: 'Other',
};

const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const formatShortDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatRelativeTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '-';

    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    });
};

const getFollowUpBadge = (lead) => {
    if (!lead || lead.status === 'converted') return null;
    const now = new Date();
    if (lead.nextFollowUpAt) {
        const due = new Date(lead.nextFollowUpAt);
        if (!Number.isNaN(due.getTime()) && due <= now) {
            return { label: 'Overdue', className: 'crm-badge-overdue' };
        }
    }

    if (!lead.lastContactedAt && lead.createdAt) {
        const created = new Date(lead.createdAt);
        if (!Number.isNaN(created.getTime())) {
            const diffMs = now.getTime() - created.getTime();
            if (diffMs > 24 * 60 * 60 * 1000) {
                return { label: 'Uncontacted', className: 'crm-badge-uncontacted' };
            }
        }
    }

    return null;
};

const clampScore = (score) => Math.max(0, Math.min(100, Number(score) || 0));

const dealStages = ['new', 'qualified', 'proposal', 'site_visit', 'negotiation', 'won', 'lost'];
const dealStageLabels = {
    new: 'New',
    qualified: 'Qualified',
    proposal: 'Proposal',
    site_visit: 'Site Visit',
    negotiation: 'Negotiation',
    won: 'Won',
    lost: 'Lost',
};

const formatCurrency = (value, currency = 'INR') => {
    if (value === null || value === undefined || value === '') return '-';
    const number = Number(value);
    if (Number.isNaN(number)) return '-';
    return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency,
        maximumFractionDigits: 0,
    }).format(number);
};

const inferActivityKind = (activity) => {
    if (activity?.kind === 'followup') return 'scheduled';
    if (activity?.kind) return activity.kind;
    if (activity?.dueAt || activity?.dueDate || activity?.dueTime) return 'scheduled';
    return 'log';
};

const getDueAtEpoch = (activity) => {
    if (!activity) return null;
    if (typeof activity.dueAt === 'number') return activity.dueAt;
    if (activity.dueAt) {
        const parsed = new Date(activity.dueAt);
        if (!Number.isNaN(parsed.getTime())) return parsed.getTime();
    }
    if (activity.dueDate && activity.dueTime) {
        const parsed = new Date(`${activity.dueDate} ${activity.dueTime}`);
        if (!Number.isNaN(parsed.getTime())) return parsed.getTime();
    }
    if (activity.dueDate) {
        const parsed = new Date(activity.dueDate);
        if (!Number.isNaN(parsed.getTime())) return parsed.getTime();
    }
    return null;
};

const toIsoString = (value) => {
    if (!value && value !== 0) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date.toISOString();
};

const LeadDetailPage = () => {
    const { id } = useParams();
    const history = useHistory();
    const { detail, loading, error, refetch } = useLeadDetail(id);
    const currentUser = useMemo(() => getStoredAuthUser(), []);
    const {
        update,
        confirmVisit,
        completeVisit,
        createDealFromMatch,
        executeAction,
        loading: mutationLoading,
    } = useLeadMutations();
    const { create: createActivity, createLog: createLogActivity, update: updateActivity, complete: completeActivity } = useActivityMutations();
    const { showSuccess, showError } = useToast();
    const { confirm, ConfirmDialog } = useConfirm();

    const [activeTab, setActiveTab] = useState('overview');
    const [showEditModal, setShowEditModal] = useState(false);
    const [showConvertModal, setShowConvertModal] = useState(false);
    const [showLogModal, setShowLogModal] = useState(false);
    const [showScheduleCategoryModal, setShowScheduleCategoryModal] = useState(false);
    const [showScheduleModal, setShowScheduleModal] = useState(false);
    const [scheduleCategory, setScheduleCategory] = useState(null);
    const [scheduleChannel, setScheduleChannel] = useState('call');
    const [scheduleProperty, setScheduleProperty] = useState(null);
    const [reschedulingActivity, setReschedulingActivity] = useState(null);
    const [scheduleDraft, setScheduleDraft] = useState({
        date: '',
        time: '',
        location: '',
        attendees: '',
        notes: '',
        followupType: '',
        customFollowupLabel: '',
        mapsUrl: '',
        afterVisitFollowup: true,
    });
    const [showFeedbackModal, setShowFeedbackModal] = useState(false);
    const [activeVisit, setActiveVisit] = useState(null);
    const [matchSort, setMatchSort] = useState('score_desc');
    const [refreshingMatches, setRefreshingMatches] = useState(false);
    const [matchesList, setMatchesList] = useState([]);
    const [matchesTotal, setMatchesTotal] = useState(0);
    const [matchesLoadingMore, setMatchesLoadingMore] = useState(false);
    const [matchesLoading, setMatchesLoading] = useState(false);
    const [matchesError, setMatchesError] = useState(null);
    const [requirementsDraft, setRequirementsDraft] = useState(null);
    const [requirementsContext, setRequirementsContext] = useState({ intent: 'buy', category: 'residential' });
    const [debouncedRequirements, setDebouncedRequirements] = useState(null);
    const matchesRequestRef = useRef(0);
    const requirementsFormRef = useRef(null);
    const [requirementsDirty, setRequirementsDirty] = useState(false);
    const [showUnsavedModal, setShowUnsavedModal] = useState(false);
    const [pendingTab, setPendingTab] = useState(null);
    const [deals, setDeals] = useState([]);
    const [dealsLoading, setDealsLoading] = useState(false);
    const [dealsError, setDealsError] = useState(null);
    const [dealSearch, setDealSearch] = useState('');
    const [dealStageFilter, setDealStageFilter] = useState('all');
    const [dealSort, setDealSort] = useState('updatedAt');
    const [showCreateDealModal, setShowCreateDealModal] = useState(false);
    const [showLinkDealModal, setShowLinkDealModal] = useState(false);
    const [createDealDraft, setCreateDealDraft] = useState({
        title: '',
        stage: 'qualified',
        value: '',
        expectedCloseAt: '',
        notes: '',
        propertyId: '',
        contactId: '',
    });
    const [linkSearch, setLinkSearch] = useState('');
    const [linkResults, setLinkResults] = useState([]);
    const [linkLoading, setLinkLoading] = useState(false);
    const [confirmStageChange, setConfirmStageChange] = useState(null);

    const [convertOptions, setConvertOptions] = useState({
        createDeal: false,
        dealTitle: '',
        dealValue: 0,
    });

    const [newActivity, setNewActivity] = useState({
        type: 'call',
        title: '',
        description: '',
        dueDate: '',
    });
    const [editingLogActivity, setEditingLogActivity] = useState(null);

    const [notesDraft, setNotesDraft] = useState('');
    const [notesSaving, setNotesSaving] = useState(false);
    const [feedbackDraft, setFeedbackDraft] = useState({
        rating: '',
        liked: '',
        disliked: '',
        budgetFit: 'maybe',
        nextStep: 'call_back',
        notes: '',
    });

    const handleTabChange = async (nextTab) => {
        if (nextTab === activeTab) return;
        if (activeTab === 'requirements' && requirementsDirty) {
            setPendingTab(nextTab);
            setShowUnsavedModal(true);
            return;
        }
        setActiveTab(nextTab);
    };

    const activityTemplates = [
        {
            id: 'call-summary',
            label: 'Call summary',
            payload: {
                type: 'call',
                title: 'Call summary',
                description: 'Discussed requirements and next steps.',
            },
        },
        {
            id: 'site-visit',
            label: 'Site visit planning',
            payload: {
                type: 'meeting',
                title: 'Site visit planning',
                description: 'Coordinate site visit date, time, and attendees.',
            },
        },
        {
            id: 'doc-request',
            label: 'Document request',
            payload: {
                type: 'email',
                title: 'Document request',
                description: 'Requested documents needed to proceed.',
            },
        },
    ];

    const followupTemplates = {
        followup_1: {
            label: '1st Follow-up',
            subject: 'Following up on your property requirements',
            body: 'Hi {name}, just checking in on your property requirements. Let me know a good time to connect.',
            whatsapp: 'Hi {name}, just checking in on your property requirements. Let me know a good time to connect.',
        },
        followup_2: {
            label: '2nd Follow-up',
            subject: 'Quick follow-up on your property search',
            body: 'Hi {name}, wanted to follow up on your property search. Happy to share more options.',
            whatsapp: 'Hi {name}, wanted to follow up on your property search. Happy to share more options.',
        },
        followup_after_site_visit: {
            label: 'Follow-up after Site Visit',
            subject: 'Thanks for the site visit',
            body: 'Hi {name}, thank you for the site visit. Let me know your feedback and next steps.',
            whatsapp: 'Hi {name}, thank you for the site visit. Let me know your feedback and next steps.',
        },
        custom: {
            label: 'Custom',
            subject: 'Follow-up',
            body: 'Hi {name}, just following up.',
            whatsapp: 'Hi {name}, just following up.',
        },
    };

    const fillTemplate = (template, leadValue) => {
        if (!template) return '';
        const name = leadValue?.name || 'there';
        return template.replace(/\{name\}/g, name);
    };

    const lead = useMemo(() => {
        if (!detail?.lead) return null;
        const computed = detail.computed || {};
        const activities = detail.tabs?.activities?.items || [];
        return {
            ...detail.lead,
            createdAt: toIsoString(detail.lead.createdAt) || detail.lead.createdAt,
            updatedAt: toIsoString(detail.lead.updatedAt) || detail.lead.updatedAt,
            nextFollowUpAt: toIsoString(computed.nextFollowUpAt) || detail.lead.nextFollowUpAt,
            lastContactedAt: toIsoString(computed.lastContactedAt) || detail.lead.lastContactedAt,
            score: computed.score ?? detail.lead.score ?? 0,
            activities,
            requirements: detail.tabs?.requirements || detail.lead.requirements,
        };
    }, [detail]);

    const suggestFollowupType = useMemo(() => {
        const items = lead?.activities || [];
        const completed = items.filter((activity) => activity.completed || activity.status === 'completed');
        const hasCompletedSiteVisit = completed.some((activity) => {
            const kind = activity.kind === 'followup' ? 'scheduled' : activity.kind;
            if (kind !== 'scheduled') return false;
            return activity.category === 'site_visit'
                || activity.type === 'site_visit'
                || activity.meta?.kind === 'site_visit';
        });
        const completedFollowups = completed.filter((activity) => activity.category === 'follow_up');
        if (hasCompletedSiteVisit) return 'followup_after_site_visit';
        if (completedFollowups.length >= 1) return 'followup_2';
        return 'followup_1';
    }, [lead?.activities]);

    useEffect(() => {
        setNotesDraft(lead?.notes || '');
    }, [lead?.notes]);

    const matchesData = detail?.tabs?.matches || null;
    const matchItems = matchesList.length > 0
        ? matchesList
        : (matchesData?.results || matchesData?.items || matchesData?.matches || []);
    const sortedMatches = useMemo(() => {
        const items = [...matchItems];
        if (matchSort === 'price_asc') {
            return items.sort((a, b) => {
                const priceA = Number((a.propertyPreview || a.property || {}).price ?? Infinity);
                const priceB = Number((b.propertyPreview || b.property || {}).price ?? Infinity);
                return priceA - priceB;
            });
        }
        if (matchSort === 'price_desc') {
            return items.sort((a, b) => {
                const priceA = Number((a.propertyPreview || a.property || {}).price ?? -Infinity);
                const priceB = Number((b.propertyPreview || b.property || {}).price ?? -Infinity);
                return priceB - priceA;
            });
        }
        return items.sort((a, b) => (Number(b.score) || 0) - (Number(a.score) || 0));
    }, [matchItems, matchSort]);

    const propertyOptions = useMemo(() => {
        const map = new Map();
        sortedMatches.forEach((match) => {
            const property = match.propertySnapshot || match.propertyPreview || match.property || {};
            if (!property.id) return;
            if (map.has(property.id)) return;
            map.set(property.id, {
                id: property.id,
                title: property.title || property.name || property.propertyType || 'Property',
            });
        });
        return Array.from(map.values());
    }, [sortedMatches]);

    useEffect(() => {
        if (!requirementsDraft && matchesData && matchesList.length === 0) {
            const base = matchesData.results || matchesData.items || matchesData.matches || [];
            setMatchesList(base);
            setMatchesTotal(matchesData.totalCandidates ?? base.length);
        }
    }, [matchesData, requirementsDraft]);

    useEffect(() => {
        if (!lead?.id) return;
        const intent = lead?.ui?.activeIntent || lead?.requirements?.intent || 'buy';
        const category = lead?.ui?.activeCategory || lead?.requirements?.category || 'residential';
        setRequirementsContext({ intent, category });
        setRequirementsDraft(lead?.requirements || null);
    }, [lead?.id]);

    useEffect(() => {
        const handle = setTimeout(() => {
            setDebouncedRequirements(requirementsDraft);
        }, 400);
        return () => clearTimeout(handle);
    }, [requirementsDraft, requirementsContext.intent, requirementsContext.category]);

    const loadMatches = async ({ offset = 0, append = false, force = false } = {}) => {
        if (!id || !debouncedRequirements) return;
        const requestId = ++matchesRequestRef.current;
        try {
            if (offset === 0) {
                setMatchesLoading(true);
                setMatchesError(null);
            } else {
                setMatchesLoadingMore(true);
            }
            const payload = {
                intent: requirementsContext.intent,
                category: requirementsContext.category,
                requirements: debouncedRequirements,
            };
            const params = { limit: 20, offset, sort: matchSort, force: force ? 'true' : undefined };
            const data = await queryLeadMatches(id, payload, params);
            if (requestId !== matchesRequestRef.current) return;
            const results = data.results || data.items || data.matches || [];
            setMatchesList((prev) => (append ? [...prev, ...results] : results));
            setMatchesTotal(data.totalCandidates ?? results.length);
        } catch (err) {
            if (requestId !== matchesRequestRef.current) return;
            console.error('Failed to load matches:', err);
            setMatchesError(err.message || 'Failed to load matches');
        } finally {
            if (requestId === matchesRequestRef.current) {
                setMatchesLoading(false);
                setMatchesLoadingMore(false);
            }
        }
    };

    useEffect(() => {
        if (!id || !debouncedRequirements) return;
        setMatchesList([]);
        setMatchesTotal(0);
        loadMatches({ offset: 0, append: false });
    }, [id, debouncedRequirements, requirementsContext.intent, requirementsContext.category, matchSort]);

    const handleLoadMoreMatches = async () => {
        if (!id || matchesLoadingMore) return;
        try {
            const nextOffset = matchesList.length;
            await loadMatches({ offset: nextOffset, append: true });
        } finally {
            // handled inside loadMatches
        }
    };


    const displayName = useMemo(() => {
        if (!lead) return '';
        return lead.name || `${lead.firstName || ''} ${lead.lastName || ''}`.trim() || 'Unnamed Lead';
    }, [lead]);
    const leadNameEchoSet = useMemo(() => {
        if (!lead) return new Set();
        return new Set(
            [
                lead.name,
                displayName,
                `${lead.firstName || ''} ${lead.lastName || ''}`.trim(),
            ]
                .map((value) => String(value || '').trim().toLowerCase())
                .filter(Boolean),
        );
    }, [lead, displayName]);
    const sanitizeLeadFieldText = (value, { suppressNameEcho = true } = {}) => {
        const text = String(value ?? '').trim();
        if (!text) return '-';
        if (suppressNameEcho && leadNameEchoSet.has(text.toLowerCase())) return '-';
        return text;
    };
    const leadDetailSubtitle = [
        sanitizeLeadFieldText(lead?.jobTitle),
        sanitizeLeadFieldText(lead?.company),
    ]
        .filter((value) => value && value !== '-')
        .join(' at ') || '-';

    const activeRequirements = requirementsDraft || lead?.requirements || {};
    const activeIntent = requirementsContext.intent || activeRequirements.intent || 'buy';
    const activeCategory = requirementsContext.category || activeRequirements.category || 'residential';
    const activeTypes = activeRequirements.propertyTypes
        || activeRequirements.basics?.propertyTypes
        || [];
    const typesLabel = Array.isArray(activeTypes) && activeTypes.length > 0 ? activeTypes.join(', ') : 'All types';
    const matchIntent = activeRequirements.matchIntent
        || (activeRequirements.wantedIntent === 'rent'
            ? 'rent'
            : (activeRequirements.wantedIntent === 'buy' || activeRequirements.wantedIntent === 'invest')
                ? 'sale'
                : null);
    const intentLabel = matchIntent === 'rent' ? 'For Rent' : matchIntent === 'sale' ? 'For Sale' : 'Intent unknown';
    const handleDelete = async () => {
        const isConfirmed = await confirm({
            title: 'Delete Lead',
            message: 'Are you sure you want to delete this lead- This action cannot be undone.',
            confirmText: 'Delete Lead',
            variant: 'danger',
        });

        if (isConfirmed) {
            try {
                await executeAction(id, { type: 'delete_lead' }, `delete-${id}`);
                showSuccess('Lead deleted successfully');
                history.push(leadsPath());
            } catch (err) {
                console.error('Failed to delete lead:', err);
                showError('Failed to delete lead');
            }
        }
    };

    const handleRefreshMatches = async () => {
        if (!id) return;
        setRefreshingMatches(true);
        try {
            await recomputeLeadMatches(id);
            await loadMatches({ offset: 0, append: false, force: true });
            showSuccess('Matches refreshed');
        } catch (err) {
            console.error('Failed to refresh matches:', err);
            showError(err.message || 'Failed to refresh matches');
        } finally {
            setRefreshingMatches(false);
        }
    };

    const loadDeals = async () => {
        if (!id) return;
        setDealsLoading(true);
        setDealsError(null);
        try {
            const params = {
                q: dealSearch || undefined,
                stage: dealStageFilter !== 'all' ? dealStageFilter : undefined,
                sort: dealSort,
            };
            const data = await getLeadDeals(id, params);
            setDeals(data.items || []);
        } catch (err) {
            console.error('Failed to load deals', err);
            setDealsError('Failed to load deals');
        } finally {
            setDealsLoading(false);
        }
    };

    useEffect(() => {
        if (activeTab !== 'deals') return;
        loadDeals();
    }, [activeTab, id, dealSearch, dealStageFilter, dealSort]);


    const handleCreateDeal = async () => {
        if (!id) return;
        try {
            const payload = {
                title: createDealDraft.title || `${displayName || 'Lead'} — Deal`,
                stage: createDealDraft.stage || 'qualified',
                value: createDealDraft.value ? Number(createDealDraft.value) : undefined,
                expectedCloseAt: createDealDraft.expectedCloseAt
                    ? new Date(createDealDraft.expectedCloseAt).toISOString()
                    : undefined,
                notes: createDealDraft.notes || undefined,
                propertyId: createDealDraft.propertyId || undefined,
                contactId: createDealDraft.contactId || undefined,
            };
            await createLeadDeal(id, payload);
            showSuccess('Deal created');
            setShowCreateDealModal(false);
            setCreateDealDraft({
                title: '',
                stage: 'qualified',
                value: '',
                expectedCloseAt: '',
                notes: '',
                propertyId: '',
                contactId: '',
            });
            loadDeals();
        } catch (err) {
            console.error('Create deal failed', err);
            showError('Failed to create deal');
        }
    };

    const handleStageChange = async (deal, nextStage) => {
        if (!deal || deal.stage === nextStage) return;
        if (['won', 'lost'].includes(nextStage)) {
            setConfirmStageChange({ deal, stage: nextStage });
            return;
        }
        try {
            await updateDeal(deal.id, { stage: nextStage });
            showSuccess('Stage updated');
            loadDeals();
        } catch (err) {
            console.error('Failed to update stage', err);
            showError('Failed to update stage');
        }
    };

    const handleUnlinkDeal = async (dealId) => {
        if (!id) return;
        const confirmed = await confirm({
            title: 'Unlink Deal?',
            message: 'This will remove the deal from this lead.',
            confirmText: 'Unlink',
            cancelText: 'Cancel',
            variant: 'warning',
        });
        if (!confirmed) return;
        try {
            await unlinkLeadDeal(id, dealId);
            showSuccess('Deal unlinked');
            loadDeals();
        } catch (err) {
            console.error('Unlink deal failed', err);
            showError('Failed to unlink deal');
        }
    };

    const loadLinkResults = async () => {
        setLinkLoading(true);
        try {
            const data = await getDeals({ q: linkSearch || undefined, pageSize: 20 });
            setLinkResults(data.items || []);
        } catch (err) {
            console.error('Failed to search deals', err);
            showError('Failed to search deals');
        } finally {
            setLinkLoading(false);
        }
    };

    const handleLinkDeal = async (deal) => {
        if (!id || !deal) return;
        if (deal.leadId && deal.leadId !== id) {
            const confirmed = await confirm({
                title: 'Move Deal?',
                message: 'This deal is linked to another lead. Move it to this lead?',
                confirmText: 'Move Deal',
                cancelText: 'Cancel',
                variant: 'warning',
            });
            if (!confirmed) return;
        }
        try {
            await linkLeadDeal(id, deal.id);
            showSuccess('Deal linked');
            setShowLinkDealModal(false);
            loadDeals();
        } catch (err) {
            console.error('Link deal failed', err);
            showError('Failed to link deal');
        }
    };

    const notifyLeadsRefresh = () => {
        if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('crm:leads:refresh'));
        }
    };

    const handleEditSubmit = async (formData) => {
        try {
            await update(id, formData);
            showSuccess('Lead updated successfully');
            setShowEditModal(false);
            refetch();
            notifyLeadsRefresh();
        } catch (err) {
            console.error('Failed to update lead:', err);
            showError('Failed to update lead');
        }
    };

    const handleConvertSubmit = async () => {
        try {
            await executeAction(id, { type: 'convert_lead', ...convertOptions }, `convert-${id}`);
            showSuccess('Lead converted successfully');
            history.push(contactsPath());
        } catch (err) {
            console.error('Failed to convert lead:', err);
            showError('Failed to convert lead');
        }
    };

    const handleActivitySubmit = async () => {
        if (!newActivity.title) return;

        try {
            if (editingLogActivity) {
                await updateActivity(editingLogActivity.id, {
                    kind: 'log',
                    status: 'completed',
                    completed: true,
                    type: newActivity.type,
                    title: newActivity.title,
                    description: newActivity.description,
                    occurredAt: editingLogActivity.occurredAt,
                    createdAt: editingLogActivity.createdAt,
                    entityType: 'lead',
                    entityId: id,
                    entityName: displayName,
                });
                showSuccess('Log updated');
            } else {
                await createLogActivity({
                    leadId: id,
                    type: newActivity.type,
                    title: newActivity.title,
                    description: newActivity.description,
                    occurredAt: Date.now(),
                    entityType: 'lead',
                    entityId: id,
                    entityName: displayName,
                });
                showSuccess('Activity created successfully');
            }
            setShowLogModal(false);
            setNewActivity({ type: 'call', title: '', description: '', dueDate: '' });
            setEditingLogActivity(null);
            refetch();
            notifyLeadsRefresh();
        } catch (err) {
            console.error('Failed to create activity:', err);
            showError('Failed to log activity');
        }
    };

    const handleActivityComplete = async (activity) => {
        if (!activity) return;
        const kind = activity.kind === 'followup' ? 'scheduled' : activity.kind;
        if (kind === 'log') return;
        try {
            await completeActivity(activity.id);
            showSuccess('Activity marked as completed');
            refetch();
            notifyLeadsRefresh();
        } catch (err) {
            console.error('Failed to complete activity:', err);
            showError('Failed to complete activity');
        }
    };

    const handleOutcomeChange = async (activity, outcome) => {
        if (!activity) return;
        try {
            await updateActivity(activity.id, { outcome });
            showSuccess('Outcome updated');
            refetch();
            notifyLeadsRefresh();
        } catch (err) {
            console.error('Failed to update outcome:', err);
            showError('Failed to update outcome');
        }
    };

    const handleTimelinePrimaryAction = async (activity, channelKey) => {
        if (!activity) return;
        const email = lead?.email;
        const phone = lead?.phone || lead?.mobile;
        const meta = activity.meta || {};
        const template = meta.templateKey ? followupTemplates[meta.templateKey] : null;
        const subject = meta.prefillSubject || (template ? fillTemplate(template.subject, lead) : '');
        const body = meta.prefillBody || (template ? fillTemplate(template.body, lead) : '');
        const waText = meta.prefillWhatsAppText || (template ? fillTemplate(template.whatsapp, lead) : '');
        try {
            if (channelKey === 'email') {
                if (!email) {
                    showError('No email on lead');
                    return;
                }
                const mailto = `mailto:${email}?subject=${encodeURIComponent(subject || 'Follow-up')}&body=${encodeURIComponent(body || '')}`;
                window.open(mailto, '_self');
            } else if (channelKey === 'whatsapp') {
                if (!phone) {
                    showError('No phone on lead');
                    return;
                }
                const url = `https://wa.me/${phone.replace(/[^\d]/g, '')}?text=${encodeURIComponent(waText || '')}`;
                window.open(url, '_blank', 'noopener,noreferrer');
            } else if (channelKey === 'call') {
                if (!phone) {
                    showError('No phone on lead');
                    return;
                }
                window.open(`tel:${phone}`, '_self');
            } else if (channelKey === 'site_visit' || channelKey === 'meeting') {
                if (meta.mapsUrl) {
                    window.open(meta.mapsUrl, '_blank', 'noopener,noreferrer');
                } else if (meta.locationText) {
                    const url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(meta.locationText)}`;
                    window.open(url, '_blank', 'noopener,noreferrer');
                }
            }

            await completeActivity(activity.id);
            showSuccess('Activity marked as done');
            refetch();
            notifyLeadsRefresh();
        } catch (err) {
            console.error('Failed to complete activity:', err);
            showError('Failed to update activity');
        }
    };

    const handleLogEdit = (activity) => {
        if (!activity) return;
        setEditingLogActivity(activity);
        setNewActivity({
            type: activity.type || 'call',
            title: activity.title || '',
            description: activity.description || '',
            dueDate: '',
        });
        setShowLogModal(true);
    };

    const openReschedule = (activity) => {
        if (!activity) return;
        const dueAt = activity.dueAt || activity.dueDate;
        const dueDate = activity.dueDate || (dueAt ? new Date(dueAt).toISOString().split('T')[0] : '');
        const dueTime = activity.dueTime || (dueAt ? new Date(dueAt).toTimeString().slice(0, 5) : '');
        const category = activity.category || (activity.type === 'site_visit' ? 'site_visit' : 'follow_up');
        const channel = activity.channel
            || (activity.type === 'whatsapp' ? 'whatsapp'
                : activity.type === 'email' ? 'email'
                    : activity.type === 'call' ? 'call'
                        : activity.type === 'site_visit' ? 'meeting'
                            : 'call');
        setReschedulingActivity(activity);
        setScheduleCategory(category);
        setScheduleChannel(channel);
        setScheduleProperty(null);
        setScheduleDraft({
            date: dueDate || '',
            time: dueTime || '',
            location: activity.meta?.locationText || '',
            attendees: activity.meta?.attendees || '',
            notes: activity.description || activity.notes || '',
            followupType: activity.meta?.followupType || '',
            customFollowupLabel: activity.meta?.followupTypeLabel || '',
            mapsUrl: activity.meta?.mapsUrl || '',
            afterVisitFollowup: activity.meta?.afterVisitFollowup !== false,
        });
        setShowScheduleModal(true);
    };

    const handleMarkContacted = async () => {
        try {
            await executeAction(id, { type: 'mark_contacted' }, `mark-contacted-${id}`);
            showSuccess('Lead marked as contacted');
            refetch();
            notifyLeadsRefresh();
        } catch (err) {
            console.error('Failed to mark contacted:', err);
            showError('Failed to update lead');
        }
    };

    const handleSaveNotes = async () => {
        setNotesSaving(true);
        try {
            await update(id, { notes: notesDraft });
            showSuccess('Notes saved');
            refetch();
            notifyLeadsRefresh();
        } catch (err) {
            console.error('Failed to save notes:', err);
            showError('Failed to save notes');
        } finally {
            setNotesSaving(false);
        }
    };

    const openScheduleCategory = (category, property = null) => {
        setScheduleCategory(category);
        setScheduleProperty(property);
        const defaultChannel = category === 'site_visit' ? 'meeting'
            : category === 'email' ? 'email'
                : category === 'whatsapp' ? 'whatsapp'
                    : category === 'call' ? 'call'
                        : category === 'other' ? 'other'
                            : 'call';
        setScheduleChannel(defaultChannel);
        setScheduleDraft({
            date: '',
            time: '',
            location: property ? [property.locality, property.city].filter(Boolean).join(', ') : '',
            attendees: '',
            notes: '',
            followupType: category === 'follow_up' ? suggestFollowupType : '',
            customFollowupLabel: '',
            mapsUrl: '',
            afterVisitFollowup: true,
        });
        setShowScheduleCategoryModal(false);
        setShowScheduleModal(true);
    };

    const handleScheduleSave = async () => {
        if (!scheduleCategory) {
            showError('Select an activity type');
            return;
        }
        if (!scheduleDraft.date) {
            showError('Select a date');
            return;
        }
        const time = scheduleDraft.time || '09:00';
        const scheduled = new Date(`${scheduleDraft.date}T${time}`);
        if (Number.isNaN(scheduled.getTime())) {
            showError('Invalid date/time');
            return;
        }

        const followupTemplate = scheduleCategory === 'follow_up'
            ? followupTemplates[scheduleDraft.followupType || suggestFollowupType]
            : null;
        const followupLabel = scheduleCategory === 'follow_up'
            ? (scheduleDraft.customFollowupLabel || followupTemplate?.label || 'Follow-up')
            : '';

        const subject = followupTemplate ? fillTemplate(followupTemplate.subject, lead) : '';
        const body = followupTemplate ? fillTemplate(followupTemplate.body, lead) : '';
        const waText = followupTemplate ? fillTemplate(followupTemplate.whatsapp, lead) : '';

        const title = scheduleCategory === 'follow_up'
            ? `Scheduled — ${formatShortDate(scheduled)}`
            : `Scheduled — ${formatDateTime(scheduled)}`;

        try {
            const activityPayload = {
                kind: 'scheduled',
                status: 'scheduled',
                category: scheduleCategory,
                channel: scheduleChannel,
                type: scheduleCategory === 'site_visit'
                    ? 'site_visit'
                    : scheduleCategory === 'call'
                        ? 'call'
                        : scheduleCategory === 'email'
                            ? 'email'
                            : scheduleCategory === 'whatsapp'
                                ? 'whatsapp'
                                : 'task',
                title,
                description: scheduleDraft.notes,
                dueAt: scheduled.getTime(),
                dueDate: scheduled.toISOString().split('T')[0],
                dueTime: scheduled.toTimeString().slice(0, 5),
                entityType: 'lead',
                entityId: id,
                entityName: displayName,
                leadId: id,
                meta: {
                    followupType: scheduleDraft.followupType,
                    followupTypeLabel: followupLabel,
                    templateKey: scheduleDraft.followupType,
                    prefillSubject: subject,
                    prefillBody: body,
                    prefillWhatsAppText: waText,
                    locationText: scheduleDraft.location,
                    mapsUrl: scheduleDraft.mapsUrl,
                    attendees: scheduleDraft.attendees,
                    afterVisitFollowup: scheduleDraft.afterVisitFollowup,
                    propertyId: scheduleProperty?.id,
                    propertyTitle: scheduleProperty?.title,
                },
            };

            if (reschedulingActivity) {
                await updateActivity(reschedulingActivity.id, {
                    ...activityPayload,
                    completed: false,
                    completedAt: null,
                });
                showSuccess('Activity rescheduled');
            } else {
                await createActivity(activityPayload);
                showSuccess('Activity scheduled');
            }
            setShowScheduleModal(false);
            setReschedulingActivity(null);
            refetch();
            notifyLeadsRefresh();
        } catch (err) {
            console.error('Failed to schedule activity:', err);
            showError('Failed to schedule activity');
        }
    };

    const handleSaveRequirements = async (payload) => {
        try {
            // Also update root-level intent/listingType for backend hooks
            const requirements = payload?.requirements || payload;
            const requirementsByContext = payload?.requirementsByContext;
            const ui = payload?.ui;
            await update(id, {
                requirements,
                requirementsByContext,
                ui,
                intent: requirements.intent,
                listingType: requirements.listingType
            });
            showSuccess('Requirements saved');
            refetch();
            loadMatches({ offset: 0, append: false, force: true });
            notifyLeadsRefresh();
        } catch (err) {
            console.error('Failed to save requirements:', err);
            showError('Failed to save requirements');
        }
    };


    const openWhatsAppTemplate = (templateId) => {
        if (!whatsapp) return;
        const leadName = displayName;
        const propertyLabel = scheduleProperty?.title || '';
        let message = '';
        if (templateId === 'intro') {
            message = `Hi ${leadName}, this is ${currentUser?.fullName || currentUser?.displayName || 'your advisor'} from Douglas & McCartney. Happy to help with your property requirements.`;
        } else if (templateId === 'options') {
            message = `Hi ${leadName}, I have shortlisted some options${propertyLabel ? ` including ${propertyLabel}` : ''}. When can we discuss?`;
        } else if (templateId === 'confirm') {
            message = `Hi ${leadName}, confirming your site visit${propertyLabel ? ` for ${propertyLabel}` : ''}. Please let me know if the time works for you.`;
        }

        if (message) {
            const url = `https://wa.me/${whatsapp}?text=${encodeURIComponent(message)}`;
            handleLogAndOpen('whatsapp', `WhatsApp template: ${templateId}`, url, siteVisitProperty, message);
        }
    };

    const handleLogAndOpen = async (channel, title, url, property, message) => {
        const requestKey = `smart-${channel}-${id}-${Math.floor(Date.now() / 1000)}`;
        if (url) {
            const isProtocolHandler = /^(tel:|mailto:)/i.test(url);
            if (isProtocolHandler && typeof document !== 'undefined') {
                const link = document.createElement('a');
                link.href = url;
                link.style.display = 'none';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            } else {
                window.open(url, '_blank', 'noopener,noreferrer');
            }
        }
        try {
            if (channel === 'call') {
                await executeAction(id, { type: 'log_call', note: title }, requestKey);
            } else if (channel === 'email') {
                await executeAction(id, { type: 'send_email', subject: title, body: message || '' }, requestKey);
            } else if (channel === 'whatsapp') {
                await executeAction(id, { type: 'send_whatsapp', message: message || title }, requestKey);
            }
        } catch (err) {
            console.error('Failed to log communication:', err);
        }

        // Refresh the activity timeline quietly so the new log appears without UI loading flicker.
        refetch({ silent: true });
    };

    const handleShareMatch = async (property) => {
        if (!whatsapp) return;
        const message = `Hi ${displayName}, sharing a property option: ${property?.title || 'Property'} in ${[property?.locality, property?.city].filter(Boolean).join(', ') || 'your preferred area'}.`;
        const url = `https://wa.me/${whatsapp}?text=${encodeURIComponent(message)}`;
        await handleLogAndOpen('whatsapp', `Shared property: ${property?.title || 'Property'}`, url, property, message);
    };

    const handleCreateDealFromMatch = async (property) => {
        try {
            const result = await createDealFromMatch(id, {
                propertyId: property?.id,
                propertyTitle: property?.title,
                value: property?.price || 0,
            });
            if (result?.deal?.id) {
                showSuccess('Deal created');
                history.push(`${dealsPath()}/${result.deal.id}`);
            } else {
                showSuccess('Deal created');
            }
            refetch();
        } catch (err) {
            console.error('Failed to create deal from match:', err);
            showError('Failed to create deal');
        }
    };

    const handleConfirmVisit = async (activity) => {
        if (!activity?.id) return;
        try {
            await confirmVisit(id, activity.id);
            showSuccess('Site visit confirmed');
            refetch();
        } catch (err) {
            console.error('Failed to confirm site visit:', err);
            showError('Failed to confirm site visit');
        }
    };

    const openFeedbackModal = (activity) => {
        setActiveVisit(activity);
        setFeedbackDraft({
            rating: '',
            liked: '',
            disliked: '',
            budgetFit: 'maybe',
            nextStep: 'call_back',
            notes: '',
        });
        setShowFeedbackModal(true);
    };

    const handleCompleteVisit = async () => {
        if (!activeVisit?.id) return;
        const payload = {
            feedback: {
                propertyId: activeVisit?.meta?.propertyId,
                rating: feedbackDraft.rating ? Number(feedbackDraft.rating) : undefined,
                liked: feedbackDraft.liked ? feedbackDraft.liked.split(',').map((entry) => entry.trim()).filter(Boolean) : [],
                disliked: feedbackDraft.disliked ? feedbackDraft.disliked.split(',').map((entry) => entry.trim()).filter(Boolean) : [],
                budgetFit: feedbackDraft.budgetFit,
                nextStep: feedbackDraft.nextStep,
                notes: feedbackDraft.notes || undefined,
            },
        };

        try {
            await completeVisit(id, activeVisit.id, payload);
            showSuccess('Site visit completed');
            setShowFeedbackModal(false);
            refetch();
            loadMatches({ offset: 0, append: false, force: true });
        } catch (err) {
            console.error('Failed to complete site visit:', err);
            showError('Failed to complete site visit');
        }
    };

    const overdueScheduled = useMemo(() => {
        const items = (lead?.activities || []).filter((activity) => {
            const kind = inferActivityKind(activity);
            if (kind !== 'scheduled') return false;
            const isCompleted = activity.status === 'completed' || activity.completed;
            if (isCompleted) return false;
            const dueAt = getDueAtEpoch(activity);
            return dueAt !== null && dueAt < Date.now();
        });
        return items.sort((a, b) => (getDueAtEpoch(a) || 0) - (getDueAtEpoch(b) || 0));
    }, [lead?.activities]);
    const earliestOverdue = overdueScheduled[0] ? getDueAtEpoch(overdueScheduled[0]) : null;
    const isOverdue = overdueScheduled.length > 0;

    if (loading) {
        return (
            <div className="crm-container">
                <div className="crm-loading">
                    <div className="crm-spinner"></div>
                </div>
            </div>
        );
    }

    if (error || !lead) {
        return (
            <div className="crm-container">
                <div className="crm-empty-state">
                    <h3>{error || 'Lead not found'}</h3>
                    <p>The lead you are looking for does not exist or has been deleted.</p>
                    <button
                        className="crm-btn crm-btn-primary"
                        onClick={() => history.push(leadsPath())}
                    >
                        Back to Leads
                    </button>
                </div>
            </div>
        );
    }

    const followUpBadge = getFollowUpBadge(lead);
    const lastTouch = detail?.computed?.lastTouchAt
        ? toIsoString(detail.computed.lastTouchAt)
        : (lead.lastContactedAt || lead.updatedAt || lead.createdAt);
    const score = clampScore(lead.score || 0);
    const leadShortId = lead.shortId
        ? String(lead.shortId).toUpperCase()
        : '-';
    const phone = lead.phone || lead.mobile;
    const whatsapp = phone ? phone.replace(/[^0-9]/g, '') : '';
    const leadStage = lead.leadStage || lead.status || 'new';
    return (
        <div className="crm-container">
            <div className="crm-detail-header">
                <div>
                    <button
                        className="crm-btn crm-btn-link"
                        onClick={() => history.push(leadsPath())}
                        style={{ paddingLeft: 0, marginBottom: '8px' }}
                    >
                        Back to Leads
                    </button>
                    <div className="crm-detail-title-row">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                            <h1 className="crm-detail-title">{displayName}</h1>
                            <span className={`crm-stage-pill crm-stage-${leadStage}`}>
                                {leadStage.replace('_', ' ')}
                            </span>
                        </div>
                        <div className="crm-lead-badges">
                            <span className="crm-badge crm-badge-source">
                                {sourceLabels[lead.source] || 'Other'}
                            </span>
                            {followUpBadge && (
                                <span className={`crm-badge ${followUpBadge.className}`}>
                                    {followUpBadge.label}
                                </span>
                            )}
                        </div>
                    </div>
                    <div className="crm-detail-subtitle">{leadDetailSubtitle}</div>
                </div>
                <div className="crm-page-actions">
                    <button
                        className="crm-btn crm-btn-secondary"
                        onClick={() => setShowEditModal(true)}
                    >
                        Edit
                    </button>
                    {lead.status !== 'converted' && (
                        <button
                            className="crm-btn crm-btn-success"
                            onClick={() => setShowConvertModal(true)}
                        >
                            Convert
                        </button>
                    )}
                    <button
                        className="crm-btn crm-btn-danger"
                        onClick={handleDelete}
                    >
                        Delete
                    </button>
                </div>
            </div>

            {isOverdue && (
                <div className="crm-alert-overdue">
                    <div>
                        <strong>Follow-up overdue</strong>
                        <span>Schedule or contact the lead today.</span>
                    </div>
                    <div className="crm-alert-actions">
                        {phone && (
                            <button
                                className="crm-btn crm-btn-secondary crm-btn-sm"
                                type="button"
                                onClick={() => handleLogAndOpen('call', 'Follow-up call', `tel:${phone}`)}
                            >
                                Call
                            </button>
                        )}
                        {whatsapp && (
                            <button
                                className="crm-btn crm-btn-secondary crm-btn-sm"
                                type="button"
                                onClick={() => handleLogAndOpen('whatsapp', 'Follow-up WhatsApp', `https://wa.me/${whatsapp}`)}
                            >
                                WhatsApp
                            </button>
                        )}
                        {lead.email && (
                            <button
                                className="crm-btn crm-btn-secondary crm-btn-sm"
                                type="button"
                                onClick={() => handleLogAndOpen('email', 'Follow-up email', `mailto:${lead.email}`)}
                            >
                                Email
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* KPI strip merged into profile card below */}

            <div className="crm-stack">
                <div className="crm-card crm-card-hover">
                        <div className="crm-tab-bar crm-tab-bar-even">
                            {['overview', 'requirements', 'matches', 'activities', 'notes', 'deals', 'files'].map((tab) => (
                                <button
                                    key={tab}
                                    className={`crm-tab ${activeTab === tab ? 'active' : ''}`}
                                    onClick={() => handleTabChange(tab)}
                                    type="button"
                                >
                                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                                </button>
                            ))}
                        </div>
                        <div className="crm-card-body">
                            {activeTab === 'overview' && (
                                <div style={{ display: 'grid', gap: '18px' }}>
                                    <div>
                                        <div className="crm-lead-profile">
                                            <div className="crm-lead-title">{displayName}</div>
                                            <div className="crm-lead-subtitle">
                                                {leadDetailSubtitle}
                                            </div>
                                        </div>

                                        <div className="crm-score-row" style={{ marginTop: '16px' }}>
                                            <span className="crm-score-label">Score</span>
                                            <div className="crm-score-bar">
                                                <span style={{ width: `${score}%` }} />
                                            </div>
                                            <span className="crm-score-value">{score}</span>
                                        </div>
                                    </div>

                                    <div style={{ borderTop: '1px solid var(--crm-border)', paddingTop: '16px' }}>
                                        <div className="crm-field-label" style={{ marginBottom: '10px' }}>Quick Actions</div>
                                        <div className="crm-quick-actions crm-quick-actions-grid" style={{ justifyContent: 'flex-start' }}>
                                            <button
                                                className="crm-icon-btn"
                                                type="button"
                                                title={phone ? 'Call' : 'No phone on lead'}
                                                disabled={!phone}
                                                onClick={() => handleLogAndOpen('call', 'Call lead', `tel:${phone}`)}
                                            >
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                                                </svg>
                                            </button>
                                            <button
                                                className="crm-icon-btn"
                                                type="button"
                                                title={whatsapp ? 'WhatsApp' : 'No phone on lead'}
                                                disabled={!whatsapp}
                                                onClick={() => handleLogAndOpen('whatsapp', 'WhatsApp lead', `https://wa.me/${whatsapp}`)}
                                            >
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M21 11.5a8.5 8.5 0 1 1-3.33-6.72" />
                                                    <path d="M22 12c0 4.97-4.03 9-9 9a8.96 8.96 0 0 1-4.65-1.3L3 21l1.32-4.05A8.96 8.96 0 0 1 3 12c0-4.97 4.03-9 9-9" />
                                                </svg>
                                            </button>
                                            <button
                                                className="crm-icon-btn"
                                                type="button"
                                                title={lead.email ? 'Email' : 'No email on lead'}
                                                disabled={!lead.email}
                                                onClick={() => handleLogAndOpen('email', 'Email lead', `mailto:${lead.email}`)}
                                            >
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                                                    <polyline points="22,6 12,13 2,6" />
                                                </svg>
                                            </button>
                                            <button
                                                className="crm-icon-btn"
                                                type="button"
                                                title="Add Task"
                                                onClick={() => openScheduleCategory('follow_up')}
                                            >
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M12 5v14" />
                                                    <path d="M5 12h14" />
                                                </svg>
                                            </button>
                                            <button
                                                className="crm-icon-btn"
                                                type="button"
                                                title="Edit"
                                                onClick={() => setShowEditModal(true)}
                                            >
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M12 20h9" />
                                                    <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
                                                </svg>
                                            </button>
                                            <button
                                                className="crm-icon-btn"
                                                type="button"
                                                title="Delete"
                                                onClick={handleDelete}
                                            >
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <polyline points="3 6 5 6 21 6" />
                                                    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                                                    <path d="M10 11v6" />
                                                    <path d="M14 11v6" />
                                                    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                                                </svg>
                                            </button>
                                        </div>
                                    </div>

                                    <div style={{ borderTop: '1px solid var(--crm-border)', paddingTop: '16px' }}>
                                        <div className="crm-detail-grid crm-detail-grid-compact">
                                            <div className="crm-field-group">
                                                <div className="crm-field-label">First Name</div>
                                                <div className="crm-field-value">{sanitizeLeadFieldText(lead.firstName)}</div>
                                            </div>
                                            <div className="crm-field-group">
                                                <div className="crm-field-label">Last Name</div>
                                                <div className="crm-field-value">{sanitizeLeadFieldText(lead.lastName)}</div>
                                            </div>
                                            <div className="crm-field-group">
                                                <div className="crm-field-label">Status</div>
                                                <div className="crm-field-value">
                                                    {statusLabels[lead.status] || lead.status || 'New'}
                                                </div>
                                            </div>
                                            <div className="crm-field-group">
                                                <div className="crm-field-label">Source</div>
                                                <div className="crm-field-value">
                                                    {sourceLabels[lead.source] || lead.source || 'Other'}
                                                </div>
                                            </div>
                                            <div className="crm-field-group">
                                                <div className="crm-field-label">Lead ID</div>
                                                <div className="crm-field-value">{leadShortId}</div>
                                            </div>
                                            <div className="crm-field-group">
                                                <div className="crm-field-label">Owner</div>
                                                <div className="crm-field-value">{sanitizeLeadFieldText(resolveLeadOwnerName(lead, currentUser), { suppressNameEcho: false })}</div>
                                            </div>
                                            <div className="crm-field-group">
                                                <div className="crm-field-label">Property</div>
                                                <div className="crm-field-value">{sanitizeLeadFieldText(lead.company)}</div>
                                            </div>
                                            <div className="crm-field-group">
                                                <div className="crm-field-label">Job Title</div>
                                                <div className="crm-field-value">{sanitizeLeadFieldText(lead.jobTitle)}</div>
                                            </div>
                                            <div className="crm-field-group">
                                                <div className="crm-field-label">Industry</div>
                                                <div className="crm-field-value">{sanitizeLeadFieldText(lead.industry)}</div>
                                            </div>
                                            <div className="crm-field-group">
                                                <div className="crm-field-label">Created</div>
                                                <div className="crm-field-value">{formatDateTime(lead.createdAt)}</div>
                                            </div>
                                            <div className="crm-field-group">
                                                <div className="crm-field-label">Last contact</div>
                                                <div className="crm-field-value">{formatRelativeTime(lastTouch)}</div>
                                            </div>
                                            <div className="crm-field-group">
                                                <div className="crm-field-label">Last contacted</div>
                                                <div className="crm-field-value">{formatDateTime(lead.lastContactedAt)}</div>
                                            </div>
                                            <div className="crm-field-group">
                                                <div className="crm-field-label">Next follow-up</div>
                                                <div className="crm-field-value">{formatShortDate(lead.nextFollowUpAt)}</div>
                                            </div>
                                            <div className="crm-field-group">
                                                <div className="crm-field-label">Score</div>
                                                <div className="crm-field-value">{score}</div>
                                            </div>
                                            <div className="crm-field-group">
                                                <div className="crm-field-label">Email</div>
                                                <div className="crm-field-value">{sanitizeLeadFieldText(lead.email)}</div>
                                            </div>
                                            <div className="crm-field-group">
                                                <div className="crm-field-label">Phone</div>
                                                <div className="crm-field-value">{sanitizeLeadFieldText(lead.phone)}</div>
                                            </div>
                                            <div className="crm-field-group">
                                                <div className="crm-field-label">Mobile</div>
                                                <div className="crm-field-value">{sanitizeLeadFieldText(lead.mobile)}</div>
                                            </div>
                                            <div className="crm-field-group">
                                                <div className="crm-field-label">Address</div>
                                                <div className="crm-field-value">{sanitizeLeadFieldText(lead.address)}</div>
                                            </div>
                                            <div className="crm-field-group">
                                                <div className="crm-field-label">Location</div>
                                                <div className="crm-field-value">
                                                    {sanitizeLeadFieldText([lead.city, lead.state, lead.country].filter(Boolean).join(', '))}
                                                </div>
                                            </div>
                                            <div className="crm-field-group">
                                                <div className="crm-field-label">Tags</div>
                                                <div className="crm-field-value">
                                                    {lead.tags && lead.tags.length > 0 ? (
                                                        <div className="crm-tag-list">
                                                            {lead.tags.map((tag) => (
                                                                <span key={tag} className="crm-badge crm-badge-source">{tag}</span>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        '-'
                                                    )}
                                                </div>
                                            </div>
                                            <div className="crm-field-group">
                                                <div className="crm-field-label">Notes</div>
                                                <div className="crm-field-value">{sanitizeLeadFieldText(lead.notes)}</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {activeTab === 'requirements' && (
                                <div>
                                    <LeadRequirementsForm
                                        ref={requirementsFormRef}
                                        lead={lead}
                                        onSave={handleSaveRequirements}
                                        loading={mutationLoading}
                                        onDirtyChange={setRequirementsDirty}
                                        onDraftChange={({ intent, category, requirements }) => {
                                            setRequirementsContext({ intent, category });
                                            setRequirementsDraft(requirements);
                                        }}
                                        onContextChange={({ intent, category, requirements }) => {
                                            setRequirementsContext({ intent, category });
                                            setRequirementsDraft(requirements);
                                        }}
                                    />
                                </div>
                            )}

                            {activeTab === 'matches' && (
                                <div>
                                    <div className="crm-tab-actions">
                                        <button
                                            className="crm-btn crm-btn-secondary crm-btn-sm"
                                            type="button"
                                            onClick={handleRefreshMatches}
                                            disabled={refreshingMatches}
                                        >
                                            {refreshingMatches ? 'Refreshing...' : 'Refresh matches'}
                                        </button>
                                        {matchesTotal !== undefined && (
                                            <div className="crm-inline-field">
                                                <span className="crm-help-text">Candidates: {matchesTotal}</span>
                                            </div>
                                        )}
                                        <div className="crm-inline-field">
                                            <label className="crm-label">Sort</label>
                                            <select
                                                className="crm-select"
                                                value={matchSort}
                                                onChange={(event) => setMatchSort(event.target.value)}
                                            >
                                                <option value="score_desc">Score (high to low)</option>
                                                <option value="price_asc">Price (low to high)</option>
                                                <option value="price_desc">Price (high to low)</option>
                                            </select>
                                        </div>
                                    </div>

                                    {(!activeRequirements || Object.keys(activeRequirements).length === 0) && (
                                        <div className="crm-empty-state">
                                            <h3>Add requirements to see matches</h3>
                                            <p>Capture the lead's preferences to unlock property matching.</p>
                                        </div>
                                    )}

                                    {(activeRequirements && Object.keys(activeRequirements).length > 0) && (
                                        <div className="crm-help-text" style={{ marginBottom: '8px' }}>
                                            {`${activeIntent.toUpperCase()} • ${activeCategory.toUpperCase()} • ${typesLabel} • ${intentLabel}`}
                                        </div>
                                    )}

                                    {activeRequirements && matchesLoading && (
                                        <div className="crm-loading">
                                            <div className="crm-spinner"></div>
                                        </div>
                                    )}

                                    {activeRequirements && matchesError && (
                                        <div className="crm-empty-state">
                                            <h3>{matchesError}</h3>
                                            <button className="crm-btn crm-btn-primary" onClick={() => loadMatches({ offset: 0, append: false, force: true })}>
                                                Retry
                                            </button>
                                        </div>
                                    )}

                                    {activeRequirements && !matchesLoading && !matchesError && (
                                        <>
                                            {sortedMatches.length > 0 ? (
                                                <div className="crm-matches-stack crm-matches-scroll">
                                                    {sortedMatches.map((match) => (
                                                        <div className="crm-match-row" key={match.propertyId}>
                                                            <div className="crm-match-row-grid matchRow">
                                                                <div className="matchLeft">
                                                                    <PropertyMatchCard
                                                                        match={match}
                                                                        onSchedule={(property) => openScheduleCategory('site_visit', property)}
                                                                        onShare={(property) => handleShareMatch(property)}
                                                                        onCreateDeal={(property) => handleCreateDealFromMatch(property)}
                                                                    />
                                                                </div>
                                                                <div className="crm-match-panel matchRight">
                                                                    <div className="crm-panel-header">
                                                                        <div className="crm-panel-title">Match Details</div>
                                                                        <div className="crm-panel-score">{Math.round(match.score || 0)}</div>
                                                                    </div>
                                                                    <div className="crm-panel-body">
                                                                        <div className="crm-panel-meta">
                                                                            <div className="crm-panel-meta-title">
                                                                                {match.propertySnapshot?.title || 'Property'}
                                                                            </div>
                                                                            <div className="crm-panel-meta-sub">
                                                                                {[match.propertySnapshot?.locality, match.propertySnapshot?.city].filter(Boolean).join(', ')}
                                                                                {match.propertySnapshot?.pincode ? ` • ${match.propertySnapshot.pincode}` : ''}
                                                                            </div>
                                                                            {match.propertySnapshot?.meta?.source === 'lead' && (
                                                                                <div className="crm-panel-meta-tag">
                                                                                    From Lead: {match.propertySnapshot?.meta?.sourceLeadName || 'Lead'}
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                        {[
                                                                            { key: 'locationScore', label: 'Location' },
                                                                            { key: 'pincodeScore', label: 'Pincode' },
                                                                            { key: 'budgetScore', label: 'Budget' },
                                                                            { key: 'areaScore', label: 'Area' },
                                                                            { key: 'bhkScore', label: 'BHK' },
                                                                            { key: 'intentScore', label: 'Rent/Sale' },
                                                                        ].map((item) => {
                                                                            const value = match.breakdown?.[item.key];
                                                                            const isNA = value === null || value === undefined;
                                                                            const percent = isNA ? 0 : Math.round(Number(value) * 100);
                                                                            return (
                                                                                <div className="crm-panel-row" key={item.key}>
                                                                                    <div className="crm-panel-label">
                                                                                        {item.label} {isNA ? <span className="crm-panel-na">Not provided</span> : `${percent}%`}
                                                                                    </div>
                                                                                    <div className={`crm-panel-bar ${isNA ? 'crm-panel-bar-disabled' : ''}`}>
                                                                                        <span style={{ width: `${percent}%` }} />
                                                                                    </div>
                                                                                </div>
                                                                            );
                                                                        })}
                                                                        {match.breakdown?.intentStatus === 'mismatch' && (
                                                                            <div className="crm-panel-warning">Intent mismatch</div>
                                                                        )}
                                                                        <div className="crm-panel-actions">
                                                                            <button
                                                                                className="crm-btn crm-btn-primary crm-btn-sm"
                                                                                type="button"
                                                                                onClick={() => openScheduleCategory('follow_up')}
                                                                            >
                                                                                + Add Follow-up
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                    {matchesList.length < matchesTotal && (
                                                        <div className="crm-match-loadmore">
                                                            <button
                                                                className="crm-btn crm-btn-secondary crm-btn-sm"
                                                                type="button"
                                                                onClick={handleLoadMoreMatches}
                                                                disabled={matchesLoadingMore}
                                                            >
                                                                {matchesLoadingMore ? 'Loading...' : 'Load more'}
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <div className="crm-empty-state">
                                                    <h3>{activeIntent === 'sell' ? 'This lead is a listing' : 'No matches for this requirement'}</h3>
                                                    <p>{activeIntent === 'sell' ? 'Use Properties to manage listings.' : 'Edit requirements or clear filters to broaden matches.'}</p>
                                                    <button
                                                        className="crm-btn crm-btn-primary crm-btn-sm"
                                                        type="button"
                                                        onClick={() => openScheduleCategory('follow_up')}
                                                    >
                                                        + Add Follow-up
                                                    </button>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            )}

                            {activeTab === 'activities' && (
                                <div>
                                    <div className="crm-tab-actions">
                                        <button
                                            className="crm-btn crm-btn-primary crm-btn-sm"
                                            onClick={() => setShowScheduleCategoryModal(true)}
                                        >
                                            + Add Activity
                                        </button>
                                        <button
                                            className="crm-btn crm-btn-secondary crm-btn-sm"
                                            onClick={() => setShowLogModal(true)}
                                        >
                                            + Add Logs
                                        </button>
                                    </div>
                                    {overdueScheduled.length > 0 && (
                                        <div className="crm-alert-overdue" style={{ marginBottom: '12px' }}>
                                            <div>
                                                <strong>{overdueScheduled.length} overdue follow-up{overdueScheduled.length > 1 ? 's' : ''}</strong>
                                                <span>
                                                    Oldest due {earliestOverdue ? new Date(earliestOverdue).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : 'earlier'}.
                                                </span>
                                            </div>
                                            <div className="crm-alert-actions">
                                                {phone && (
                                                    <button
                                                        className="crm-btn crm-btn-secondary crm-btn-sm"
                                                        type="button"
                                                        onClick={() => handleLogAndOpen('call', 'Follow-up call', `tel:${phone}`)}
                                                    >
                                                        Call
                                                    </button>
                                                )}
                                                {whatsapp && (
                                                    <button
                                                        className="crm-btn crm-btn-secondary crm-btn-sm"
                                                        type="button"
                                                        onClick={() => handleLogAndOpen('whatsapp', 'Follow-up WhatsApp', `https://wa.me/${whatsapp}`)}
                                                    >
                                                        WhatsApp
                                                    </button>
                                                )}
                                                {lead.email && (
                                                    <button
                                                        className="crm-btn crm-btn-secondary crm-btn-sm"
                                                        type="button"
                                                        onClick={() => handleLogAndOpen('email', 'Follow-up email', `mailto:${lead.email}`)}
                                                    >
                                                        Email
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                    <div className="crm-site-visit-list">
                                        {(lead.activities || [])
                                            .filter((activity) => (activity.category === 'site_visit' || activity.type === 'site_visit') && activity.meta?.propertyId)
                                            .map((activity) => {
                                                const isCompleted = activity.status === 'completed' || activity.completed;
                                                const isConfirmed = Boolean(activity.meta?.confirmedAt);
                                                return (
                                                    <div key={activity.id} className="crm-site-visit-row">
                                                        <div>
                                                            <div className="crm-site-visit-title">{activity.title || 'Site Visit'}</div>
                                                            <div className="crm-site-visit-meta">
                                                                {activity.dueDate || '-'} {activity.dueTime || ''}
                                                                {activity.location ? ` | ${activity.location}` : ''}
                                                            </div>
                                                        </div>
                                                        <div className="crm-site-visit-actions">
                                                            <span className="crm-site-visit-status">
                                                                {isCompleted ? 'Completed' : isConfirmed ? 'Confirmed' : 'Scheduled'}
                                                            </span>
                                                            {!isCompleted && (
                                                                <button
                                                                    className="crm-btn crm-btn-secondary crm-btn-sm"
                                                                    type="button"
                                                                    onClick={() => handleConfirmVisit(activity)}
                                                                >
                                                                    Confirm
                                                                </button>
                                                            )}
                                                            {!isCompleted && (
                                                                <button
                                                                    className="crm-btn crm-btn-primary crm-btn-sm"
                                                                    type="button"
                                                                    onClick={() => openFeedbackModal(activity)}
                                                                >
                                                                    Mark Completed
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                    </div>
                                    <div className="crm-activities-timeline">
                                        <ActivityTimeline
                                            activities={lead.activities || []}
                                            onComplete={handleActivityComplete}
                                            onPrimaryAction={handleTimelinePrimaryAction}
                                            onEdit={handleLogEdit}
                                            onReschedule={openReschedule}
                                            onOutcomeChange={handleOutcomeChange}
                                            lead={lead}
                                        />
                                    </div>
                                </div>
                            )}

                            {activeTab === 'notes' && (
                                <div>
                                    <div className="crm-form-group">
                                        <label className="crm-label">Notes</label>
                                        <textarea
                                            className="crm-textarea"
                                            rows={6}
                                            value={notesDraft}
                                            onChange={(event) => setNotesDraft(event.target.value)}
                                            placeholder="Add notes for this lead..."
                                        />
                                    </div>
                                    <div className="crm-form-actions">
                                        <button
                                            className="crm-btn crm-btn-primary"
                                            onClick={handleSaveNotes}
                                            disabled={notesSaving || notesDraft === (lead.notes || '')}
                                        >
                                            {notesSaving ? 'Saving...' : 'Save Notes'}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {activeTab === 'deals' && (
                                <div className="crm-deals-tab">
                                    <div className="crm-tab-actions" style={{ justifyContent: 'space-between', gap: '12px' }}>
                                        <div className="crm-inline-field">
                                            <strong>Deals</strong>
                                            <span className="count-badge" style={{ marginLeft: '8px' }}>{deals.length}</span>
                                        </div>
                                        <div className="crm-inline-field" style={{ gap: '8px' }}>
                                            <button
                                                className="crm-btn crm-btn-primary crm-btn-sm"
                                                type="button"
                                                onClick={() => {
                                                    setCreateDealDraft({
                                                        title: `${displayName || 'Lead'} — Deal`,
                                                        stage: 'qualified',
                                                        value: '',
                                                        expectedCloseAt: '',
                                                        notes: '',
                                                        propertyId: '',
                                                        contactId: '',
                                                    });
                                                    setShowCreateDealModal(true);
                                                }}
                                            >
                                                + Create Deal
                                            </button>
                                            <button
                                                className="crm-btn crm-btn-secondary crm-btn-sm"
                                                type="button"
                                                onClick={() => {
                                                    setShowLinkDealModal(true);
                                                    setLinkResults([]);
                                                    setLinkSearch('');
                                                }}
                                            >
                                                Link Existing Deal
                                            </button>
                                        </div>
                                    </div>

                                    <div className="crm-tab-actions" style={{ gap: '12px', flexWrap: 'wrap' }}>
                                        <input
                                            className="crm-input"
                                            style={{ minWidth: '220px' }}
                                            placeholder="Search deals..."
                                            value={dealSearch}
                                            onChange={(event) => setDealSearch(event.target.value)}
                                        />
                                        <select
                                            className="crm-input"
                                            value={dealSort}
                                            onChange={(event) => setDealSort(event.target.value)}
                                        >
                                            <option value="updatedAt">Recently updated</option>
                                            <option value="createdAt">Created newest</option>
                                            <option value="value">Value high to low</option>
                                        </select>
                                    </div>

                                    {dealsLoading && <div className="crm-empty-state">Loading deals...</div>}
                                    {!dealsLoading && dealsError && (
                                        <div className="crm-empty-state">
                                            <p>{dealsError}</p>
                                            <button className="crm-btn crm-btn-primary" onClick={loadDeals}>Retry</button>
                                        </div>
                                    )}
                                    {!dealsLoading && !dealsError && deals.length === 0 && (
                                        <div className="crm-empty-state">
                                            <h3>No deals yet</h3>
                                            <p>Create your first deal for this lead.</p>
                                            <button className="crm-btn crm-btn-primary" onClick={() => setShowCreateDealModal(true)}>
                                                + Create Deal
                                            </button>
                                        </div>
                                    )}

                                    {!dealsLoading && !dealsError && deals.length > 0 && (
                                        <div>
                                            {deals.filter((deal) => !deal.archived).map((deal) => (
                                                <div key={deal.id} className="crm-card crm-card-hover" style={{ marginBottom: '12px' }}>
                                                    <div className="crm-card-body">
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                                                            <div>
                                                                <h3 style={{ margin: 0 }}>{deal.title || 'Untitled Deal'}</h3>
                                                                <div className="crm-help-text">
                                                                    Updated {formatRelativeTime(deal.updatedAt || deal.createdAt)}
                                                                </div>
                                                            </div>
                                                            <div>
                                                                <select
                                                                    className="crm-input"
                                                                    value={deal.stage || 'new'}
                                                                    onChange={(event) => handleStageChange(deal, event.target.value)}
                                                                >
                                                                    {dealStages.map((stage) => (
                                                                        <option key={stage} value={stage}>{dealStageLabels[stage] || stage}</option>
                                                                    ))}
                                                                </select>
                                                            </div>
                                                        </div>

                                                        <div style={{ display: 'flex', gap: '16px', marginTop: '12px', flexWrap: 'wrap' }}>
                                                            <div>
                                                                <div className="crm-field-label">Value</div>
                                                                <div className="crm-field-value">{formatCurrency(deal.value, deal.currency || 'INR')}</div>
                                                            </div>
                                                            <div>
                                                                <div className="crm-field-label">Expected close</div>
                                                                <div className="crm-field-value">{formatShortDate(deal.expectedCloseAt || deal.expectedCloseDate)}</div>
                                                            </div>
                                                        </div>

                                                        <div style={{ marginTop: '12px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                                            <span className="crm-badge crm-badge-source">Lead: {displayName || 'Lead'}</span>
                                                            {deal.contactName && (
                                                                <span className="crm-badge crm-badge-source">Contact: {deal.contactName}</span>
                                                            )}
                                                            {deal.propertyName && (
                                                                <span className="crm-badge crm-badge-source">Property: {deal.propertyName}</span>
                                                            )}
                                                        </div>

                                                        <div style={{ marginTop: '12px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                                            <button className="crm-btn crm-btn-secondary crm-btn-sm" onClick={() => history.push(dealPath(deal.id))}>
                                                                Open Deal
                                                            </button>
                                                            <button className="crm-btn crm-btn-secondary crm-btn-sm" onClick={() => openScheduleCategory('follow_up')}>
                                                                Add Activity
                                                            </button>
                                                            <button className="crm-btn crm-btn-secondary crm-btn-sm" onClick={() => handleUnlinkDeal(deal.id)}>
                                                                Unlink
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}

                                            {deals.some((deal) => deal.archived) && (
                                                <div className="crm-card" style={{ marginTop: '16px' }}>
                                                    <div className="crm-card-header">
                                                        <h3 className="crm-card-title">Past Deals</h3>
                                                    </div>
                                                    <div className="crm-card-body">
                                                        {deals.filter((deal) => deal.archived).map((deal) => (
                                                            <div key={deal.id} className="crm-card crm-card-hover" style={{ marginBottom: '12px' }}>
                                                                <div className="crm-card-body">
                                                                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                                                                        <div>
                                                                            <h3 style={{ margin: 0 }}>{deal.title || 'Untitled Deal'}</h3>
                                                                            <div className="crm-help-text">
                                                                                Archived {formatRelativeTime(deal.archivedAt || deal.updatedAt || deal.createdAt)}
                                                                            </div>
                                                                        </div>
                                                                        <div>
                                                                            <span className="crm-badge crm-badge-muted">Archived</span>
                                                                        </div>
                                                                    </div>

                                                                    <div style={{ display: 'flex', gap: '16px', marginTop: '12px', flexWrap: 'wrap' }}>
                                                                        <div>
                                                                            <div className="crm-field-label">Value</div>
                                                                            <div className="crm-field-value">{formatCurrency(deal.value, deal.currency || 'INR')}</div>
                                                                        </div>
                                                                        <div>
                                                                            <div className="crm-field-label">Expected close</div>
                                                                            <div className="crm-field-value">{formatShortDate(deal.expectedCloseAt || deal.expectedCloseDate)}</div>
                                                                        </div>
                                                                    </div>

                                                                    <div style={{ marginTop: '12px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                                                        <button className="crm-btn crm-btn-secondary crm-btn-sm" onClick={() => history.push(dealPath(deal.id))}>
                                                                            Open Deal
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}

                            {activeTab === 'files' && (
                                <FilesPanel
                                    entityType="lead"
                                    entityId={id}
                                    ownerName={resolveLeadOwnerName(lead, currentUser)}
                                />
                            )}
                        </div>
                    </div>
            </div>
            {showEditModal && (
                <div className="crm-modal-overlay" onClick={() => setShowEditModal(false)}>
                    <div
                        className="crm-modal"
                        onClick={(event) => event.stopPropagation()}
                        style={{ background: 'var(--crm-bg)', maxWidth: '600px', width: '100%' }}
                    >
                        <div className="crm-modal-header">
                            <h2 className="crm-modal-title">Edit Lead</h2>
                            <button className="crm-modal-close" onClick={() => setShowEditModal(false)}>X</button>
                        </div>
                        <div className="crm-modal-body">
                            <LeadForm
                                lead={lead}
                                onSubmit={handleEditSubmit}
                                onCancel={() => setShowEditModal(false)}
                                loading={mutationLoading}
                            />
                        </div>
                    </div>
                </div>
            )}

            {showConvertModal && (
                <div className="crm-modal-overlay" onClick={() => setShowConvertModal(false)}>
                    <div
                        className="crm-modal"
                        onClick={(event) => event.stopPropagation()}
                        style={{ background: 'var(--crm-bg)', maxWidth: '420px', width: '100%' }}
                    >
                        <div className="crm-modal-header">
                            <h2 className="crm-modal-title">Convert Lead</h2>
                            <button className="crm-modal-close" onClick={() => setShowConvertModal(false)}>X</button>
                        </div>
                        <div className="crm-modal-body">
                            <p style={{ marginBottom: '16px', color: 'var(--crm-gray-700)' }}>
                                This will convert the lead into a Contact.
                            </p>

                            <label className="crm-inline-check" style={{ marginBottom: '16px' }}>
                                <input
                                    type="checkbox"
                                    checked={convertOptions.createDeal}
                                    onChange={(event) => setConvertOptions({ ...convertOptions, createDeal: event.target.checked })}
                                />
                                <span>Also create a Deal</span>
                            </label>

                            {convertOptions.createDeal && (
                                <div style={{ marginBottom: '16px' }}>
                                    <div className="crm-form-group">
                                        <label className="crm-label required">Deal Title</label>
                                        <input
                                            type="text"
                                            className="crm-input"
                                            value={convertOptions.dealTitle}
                                            onChange={(event) => setConvertOptions({ ...convertOptions, dealTitle: event.target.value })}
                                            placeholder="e.g. Residential sale"
                                        />
                                    </div>
                                    <div className="crm-form-group">
                                        <label className="crm-label">Value</label>
                                        <input
                                            type="number"
                                            className="crm-input"
                                            value={convertOptions.dealValue}
                                            onChange={(event) => setConvertOptions({ ...convertOptions, dealValue: Number(event.target.value) })}
                                            placeholder="0"
                                        />
                                    </div>
                                </div>
                            )}

                            <div className="crm-form-actions">
                                <button
                                    className="crm-btn crm-btn-secondary"
                                    onClick={() => setShowConvertModal(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    className="crm-btn crm-btn-success"
                                    onClick={handleConvertSubmit}
                                    disabled={convertOptions.createDeal && !convertOptions.dealTitle}
                                >
                                    {mutationLoading ? 'Converting...' : 'Convert Lead'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showCreateDealModal && (
                <div className="crm-modal-overlay" onClick={() => setShowCreateDealModal(false)}>
                    <div
                        className="crm-modal"
                        onClick={(event) => event.stopPropagation()}
                        style={{
                            maxWidth: '520px',
                            background: 'var(--crm-bg)',
                            color: 'var(--crm-text, #fff)',
                            border: '1px solid var(--crm-gray-800, #2a2a2a)',
                        }}
                    >
                        <div className="crm-modal-header" style={{ background: 'var(--crm-bg)', borderBottom: '1px solid var(--crm-gray-800, #2a2a2a)' }}>
                            <h2 className="crm-modal-title">Create Deal</h2>
                            <button className="crm-modal-close" onClick={() => setShowCreateDealModal(false)}>X</button>
                        </div>
                        <div className="crm-modal-body">
                            <div className="crm-form-grid">
                                <div className="crm-form-group">
                                    <label className="crm-label">Title</label>
                                    <input
                                        className="crm-input"
                                        value={createDealDraft.title}
                                        onChange={(event) => setCreateDealDraft({ ...createDealDraft, title: event.target.value })}
                                        placeholder={`${displayName || 'Lead'} — Deal`}
                                    />
                                </div>
                                <div className="crm-form-group">
                                    <label className="crm-label">Stage</label>
                                    <select
                                        className="crm-input"
                                        value={createDealDraft.stage}
                                        onChange={(event) => setCreateDealDraft({ ...createDealDraft, stage: event.target.value })}
                                    >
                                        {dealStages.map((stage) => (
                                            <option key={stage} value={stage}>{dealStageLabels[stage] || stage}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="crm-form-group">
                                    <label className="crm-label">Value (₹)</label>
                                    <input
                                        className="crm-input"
                                        type="number"
                                        value={createDealDraft.value}
                                        onChange={(event) => setCreateDealDraft({ ...createDealDraft, value: event.target.value })}
                                        placeholder="0"
                                    />
                                </div>
                                <div className="crm-form-group">
                                    <label className="crm-label">Expected close</label>
                                    <input
                                        className="crm-input"
                                        type="date"
                                        value={createDealDraft.expectedCloseAt}
                                        onChange={(event) => setCreateDealDraft({ ...createDealDraft, expectedCloseAt: event.target.value })}
                                    />
                                </div>
                                {propertyOptions.length > 0 && (
                                    <div className="crm-form-group">
                                        <label className="crm-label">Attach property</label>
                                        <select
                                            className="crm-input"
                                            value={createDealDraft.propertyId}
                                            onChange={(event) => setCreateDealDraft({ ...createDealDraft, propertyId: event.target.value })}
                                        >
                                            <option value="">None</option>
                                            {propertyOptions.map((property) => (
                                                <option key={property.id} value={property.id}>{property.title}</option>
                                            ))}
                                        </select>
                                    </div>
                                )}
                                <div className="crm-form-group">
                                    <label className="crm-label">Notes</label>
                                    <textarea
                                        className="crm-input"
                                        rows={4}
                                        value={createDealDraft.notes}
                                        onChange={(event) => setCreateDealDraft({ ...createDealDraft, notes: event.target.value })}
                                        placeholder="Notes"
                                    />
                                </div>
                            </div>
                            <div className="crm-form-actions">
                                <button className="crm-btn crm-btn-secondary" onClick={() => setShowCreateDealModal(false)}>Cancel</button>
                                <button className="crm-btn crm-btn-primary" onClick={handleCreateDeal}>Create Deal</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showLinkDealModal && (
                <div className="crm-modal-overlay" onClick={() => setShowLinkDealModal(false)}>
                    <div
                        className="crm-modal"
                        onClick={(event) => event.stopPropagation()}
                        style={{
                            maxWidth: '520px',
                            background: 'var(--crm-bg)',
                            color: 'var(--crm-text, #fff)',
                            border: '1px solid var(--crm-gray-800, #2a2a2a)',
                        }}
                    >
                        <div className="crm-modal-header" style={{ background: 'var(--crm-bg)', borderBottom: '1px solid var(--crm-gray-800, #2a2a2a)' }}>
                            <h2 className="crm-modal-title">Link Existing Deal</h2>
                            <button className="crm-modal-close" onClick={() => setShowLinkDealModal(false)}>X</button>
                        </div>
                        <div className="crm-modal-body">
                            <div className="crm-form-group">
                                <label className="crm-label">Search deals</label>
                                <input
                                    className="crm-input"
                                    value={linkSearch}
                                    onChange={(event) => setLinkSearch(event.target.value)}
                                    placeholder="Search by title..."
                                />
                                <button className="crm-btn crm-btn-secondary crm-btn-sm" style={{ marginTop: '8px' }} onClick={loadLinkResults}>
                                    Search
                                </button>
                            </div>
                            {linkLoading && <div className="crm-empty-state">Searching...</div>}
                            {!linkLoading && linkResults.length === 0 && (
                                <div className="crm-empty-state">
                                    <p>No deals found.</p>
                                </div>
                            )}
                            {!linkLoading && linkResults.length > 0 && (
                                <div style={{ maxHeight: '260px', overflowY: 'auto' }}>
                                    {linkResults.map((deal) => (
                                        <div key={deal.id} className="crm-card crm-card-hover" style={{ marginBottom: '8px' }}>
                                            <div className="crm-card-body" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <div>
                                                    <strong>{deal.title || 'Untitled Deal'}</strong>
                                                    <div className="crm-help-text">{dealStageLabels[deal.stage] || deal.stage}</div>
                                                </div>
                                                <button className="crm-btn crm-btn-primary crm-btn-sm" onClick={() => handleLinkDeal(deal)}>
                                                    Link
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <ConfirmModal
                isOpen={!!confirmStageChange}
                onClose={() => setConfirmStageChange(null)}
                onConfirm={async () => {
                    const data = confirmStageChange;
                    if (!data) return;
                    try {
                        await updateDeal(data.deal.id, { stage: data.stage, final: true });
                        showSuccess('Stage updated');
                        setConfirmStageChange(null);
                        loadDeals();
                    } catch (err) {
                        console.error('Failed to update stage', err);
                        showError('Failed to update stage');
                    }
                }}
                onCancel={() => setConfirmStageChange(null)}
                title="Confirm Final Stage"
                message="Marking a deal as won/lost is final. Continue?"
                confirmText="Yes, update"
                cancelText="Cancel"
            />

            {showLogModal && (
                <div className="crm-modal-overlay" onClick={() => setShowLogModal(false)}>
                    <div
                        className="crm-modal"
                        onClick={(event) => event.stopPropagation()}
                        style={{ background: 'var(--crm-bg)', maxWidth: '560px', width: '100%' }}
                    >
                        <div className="crm-modal-header">
                            <h2 className="crm-modal-title">{editingLogActivity ? 'Edit Log' : 'Log Activity'}</h2>
                            <button
                                className="crm-modal-close"
                                onClick={() => {
                                    setShowLogModal(false);
                                    setEditingLogActivity(null);
                                }}
                            >
                                X
                            </button>
                        </div>
                        <div className="crm-modal-body">
                            <div className="crm-template-row">
                                {activityTemplates.map((template) => (
                                    <button
                                        key={template.id}
                                        className="crm-btn crm-btn-secondary crm-btn-sm"
                                        type="button"
                                        onClick={() => setNewActivity((prev) => ({ ...prev, ...template.payload }))}
                                    >
                                        {template.label}
                                    </button>
                                ))}
                            </div>

                            <div className="crm-form-group">
                                <label className="crm-label">Type</label>
                                <select
                                    className="crm-select"
                                    value={newActivity.type}
                                    onChange={(event) => setNewActivity({ ...newActivity, type: event.target.value })}
                                >
                                    <option value="call">Phone Call</option>
                                    <option value="email">Email</option>
                                    <option value="meeting">Meeting</option>
                                    <option value="task">Task</option>
                                    <option value="note">Note</option>
                                </select>
                            </div>

                            <div className="crm-form-group">
                                <label className="crm-label required">Title</label>
                                <input
                                    type="text"
                                    className="crm-input"
                                    placeholder="Summary of activity"
                                    value={newActivity.title}
                                    onChange={(event) => setNewActivity({ ...newActivity, title: event.target.value })}
                                />
                            </div>

                            <div className="crm-form-group">
                                <label className="crm-label">Description</label>
                                <textarea
                                    className="crm-textarea"
                                    placeholder="Details..."
                                    value={newActivity.description}
                                    onChange={(event) => setNewActivity({ ...newActivity, description: event.target.value })}
                                />
                            </div>

                            <div className="crm-form-actions">
                                <button
                                    className="crm-btn crm-btn-secondary"
                                    onClick={() => {
                                        setShowLogModal(false);
                                        setEditingLogActivity(null);
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    className="crm-btn crm-btn-primary"
                                    onClick={handleActivitySubmit}
                                    disabled={!newActivity.title}
                                >
                                    {editingLogActivity ? 'Save Changes' : 'Save Activity'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showScheduleCategoryModal && (
                <div className="crm-modal-overlay" onClick={() => setShowScheduleCategoryModal(false)}>
                    <div
                        className="crm-modal"
                        onClick={(event) => event.stopPropagation()}
                        style={{ background: 'var(--crm-bg)', maxWidth: '420px', width: '100%' }}
                    >
                        <div className="crm-modal-header">
                            <h2 className="crm-modal-title">Add Activity</h2>
                            <button className="crm-modal-close" onClick={() => setShowScheduleCategoryModal(false)}>X</button>
                        </div>
                        <div className="crm-modal-body">
                            <div className="crm-template-row">
                                {[
                                    { id: 'follow_up', label: 'Schedule Follow-up' },
                                    { id: 'site_visit', label: 'Schedule Site Visit' },
                                ].map((item) => (
                                    <button
                                        key={item.id}
                                        className="crm-btn crm-btn-secondary crm-btn-sm"
                                        type="button"
                                        onClick={() => openScheduleCategory(item.id)}
                                    >
                                        {item.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showScheduleModal && (
                <div
                    className="crm-modal-overlay"
                    onClick={() => {
                        setShowScheduleModal(false);
                        setReschedulingActivity(null);
                    }}
                >
                    <div
                        className="crm-modal"
                        onClick={(event) => event.stopPropagation()}
                        style={{ background: 'var(--crm-bg)', maxWidth: '560px', width: '100%' }}
                    >
                        <div className="crm-modal-header">
                            <h2 className="crm-modal-title">
                                {reschedulingActivity ? 'Reschedule Activity' : 'Schedule Activity'}
                            </h2>
                            <button
                                className="crm-modal-close"
                                onClick={() => {
                                    setShowScheduleModal(false);
                                    setReschedulingActivity(null);
                                }}
                            >
                                X
                            </button>
                        </div>
                        <div className="crm-modal-body">
                            <div className="crm-tab-bar" style={{ padding: 0, marginBottom: '12px' }}>
                                {[
                                    { id: 'call', label: 'Call' },
                                    { id: 'whatsapp', label: 'WhatsApp' },
                                    { id: 'email', label: 'Email' },
                                    { id: 'meeting', label: 'Meeting' },
                                    { id: 'other', label: 'Other' },
                                ].map((tab) => (
                                    <button
                                        key={tab.id}
                                        className={`crm-tab ${scheduleChannel === tab.id ? 'active' : ''}`}
                                        onClick={() => setScheduleChannel(tab.id)}
                                        type="button"
                                    >
                                        {tab.label}
                                    </button>
                                ))}
                            </div>

                            {scheduleCategory === 'follow_up' && (
                                <div className="crm-form-group">
                                    <label className="crm-label">Follow-up Type</label>
                                    <div className="crm-muted" style={{ marginBottom: '6px' }}>
                                        Suggested: {followupTemplates[suggestFollowupType]?.label || 'Follow-up'}
                                    </div>
                                    <select
                                        className="crm-select"
                                        value={scheduleDraft.followupType || suggestFollowupType}
                                        onChange={(event) => setScheduleDraft((prev) => ({ ...prev, followupType: event.target.value }))}
                                    >
                                        {Object.keys(followupTemplates).map((key) => (
                                            <option key={key} value={key}>
                                                {followupTemplates[key].label}
                                            </option>
                                        ))}
                                    </select>
                                    {(scheduleDraft.followupType || suggestFollowupType) === 'custom' && (
                                        <input
                                            className="crm-input"
                                            style={{ marginTop: '8px' }}
                                            placeholder="Custom follow-up label"
                                            value={scheduleDraft.customFollowupLabel}
                                            onChange={(event) => setScheduleDraft((prev) => ({ ...prev, customFollowupLabel: event.target.value }))}
                                        />
                                    )}
                                </div>
                            )}

                            <div className="crm-form-row">
                                <div className="crm-form-group">
                                    <label className="crm-label required">Date</label>
                                    <input
                                        type="date"
                                        className="crm-input"
                                        value={scheduleDraft.date}
                                        onChange={(event) => setScheduleDraft((prev) => ({ ...prev, date: event.target.value }))}
                                    />
                                </div>
                                <div className="crm-form-group">
                                    <label className="crm-label">Time</label>
                                    <input
                                        type="time"
                                        className="crm-input"
                                        value={scheduleDraft.time}
                                        onChange={(event) => setScheduleDraft((prev) => ({ ...prev, time: event.target.value }))}
                                    />
                                </div>
                            </div>

                            {scheduleCategory === 'site_visit' && (
                                <>
                                    <div className="crm-form-group">
                                        <label className="crm-label">Location</label>
                                        <input
                                            className="crm-input"
                                            value={scheduleDraft.location}
                                            onChange={(event) => setScheduleDraft((prev) => ({ ...prev, location: event.target.value }))}
                                            placeholder="Address or meeting point"
                                        />
                                    </div>
                                    <div className="crm-form-group">
                                        <label className="crm-label">Maps URL</label>
                                        <input
                                            className="crm-input"
                                            value={scheduleDraft.mapsUrl}
                                            onChange={(event) => setScheduleDraft((prev) => ({ ...prev, mapsUrl: event.target.value }))}
                                            placeholder="https://maps.google.com/..."
                                        />
                                    </div>
                                    <div className="crm-form-group">
                                        <label className="crm-label">Attendees</label>
                                        <input
                                            className="crm-input"
                                            value={scheduleDraft.attendees}
                                            onChange={(event) => setScheduleDraft((prev) => ({ ...prev, attendees: event.target.value }))}
                                            placeholder="Comma separated names"
                                        />
                                    </div>
                                    <div className="crm-form-group">
                                        <label className="crm-label">Notes</label>
                                        <textarea
                                            className="crm-textarea"
                                            rows={3}
                                            value={scheduleDraft.notes}
                                            onChange={(event) => setScheduleDraft((prev) => ({ ...prev, notes: event.target.value }))}
                                            placeholder="Any notes for the visit"
                                        />
                                    </div>
                                    <div className="crm-form-group">
                                        <label className="crm-checkbox">
                                            <input
                                                type="checkbox"
                                                checked={scheduleDraft.afterVisitFollowup}
                                                onChange={(event) => setScheduleDraft((prev) => ({ ...prev, afterVisitFollowup: event.target.checked }))}
                                            />
                                            Suggest follow-up after visit
                                        </label>
                                    </div>
                                </>
                            )}

                            {scheduleCategory !== 'site_visit' && (
                                <div className="crm-form-group">
                                    <label className="crm-label">Notes</label>
                                    <textarea
                                        className="crm-textarea"
                                        rows={3}
                                        value={scheduleDraft.notes}
                                        onChange={(event) => setScheduleDraft((prev) => ({ ...prev, notes: event.target.value }))}
                                        placeholder="Optional notes"
                                    />
                                </div>
                            )}

                            <div className="crm-form-actions">
                                <button
                                    className="crm-btn crm-btn-secondary"
                                    onClick={() => {
                                        setShowScheduleModal(false);
                                        setReschedulingActivity(null);
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    className="crm-btn crm-btn-primary"
                                    onClick={handleScheduleSave}
                                >
                                    {reschedulingActivity ? 'Reschedule Activity' : 'Schedule Activity'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showFeedbackModal && (
                <div className="crm-modal-overlay" onClick={() => setShowFeedbackModal(false)}>
                    <div
                        className="crm-modal"
                        onClick={(event) => event.stopPropagation()}
                        style={{ background: 'var(--crm-bg)', maxWidth: '520px', width: '100%' }}
                    >
                        <div className="crm-modal-header">
                            <h2 className="crm-modal-title">Site Visit Feedback</h2>
                            <button className="crm-modal-close" onClick={() => setShowFeedbackModal(false)}>X</button>
                        </div>
                        <div className="crm-modal-body">
                            <div className="crm-form-row">
                                <div className="crm-form-group">
                                    <label className="crm-label">Rating</label>
                                    <select
                                        className="crm-select"
                                        value={feedbackDraft.rating}
                                        onChange={(event) => setFeedbackDraft((prev) => ({ ...prev, rating: event.target.value }))}
                                    >
                                        <option value="">Select</option>
                                        <option value="1">1</option>
                                        <option value="2">2</option>
                                        <option value="3">3</option>
                                        <option value="4">4</option>
                                        <option value="5">5</option>
                                    </select>
                                </div>
                                <div className="crm-form-group">
                                    <label className="crm-label">Budget Fit</label>
                                    <select
                                        className="crm-select"
                                        value={feedbackDraft.budgetFit}
                                        onChange={(event) => setFeedbackDraft((prev) => ({ ...prev, budgetFit: event.target.value }))}
                                    >
                                        <option value="yes">Yes</option>
                                        <option value="no">No</option>
                                        <option value="maybe">Maybe</option>
                                    </select>
                                </div>
                            </div>

                            <div className="crm-form-group">
                                <label className="crm-label">Liked (comma separated)</label>
                                <input
                                    className="crm-input"
                                    value={feedbackDraft.liked}
                                    onChange={(event) => setFeedbackDraft((prev) => ({ ...prev, liked: event.target.value }))}
                                    placeholder="e.g. location, layout"
                                />
                            </div>
                            <div className="crm-form-group">
                                <label className="crm-label">Disliked (comma separated)</label>
                                <input
                                    className="crm-input"
                                    value={feedbackDraft.disliked}
                                    onChange={(event) => setFeedbackDraft((prev) => ({ ...prev, disliked: event.target.value }))}
                                    placeholder="e.g. price, parking"
                                />
                            </div>

                            <div className="crm-form-group">
                                <label className="crm-label">Next Step</label>
                                <select
                                    className="crm-select"
                                    value={feedbackDraft.nextStep}
                                    onChange={(event) => setFeedbackDraft((prev) => ({ ...prev, nextStep: event.target.value }))}
                                >
                                    <option value="call_back">Call back</option>
                                    <option value="share_more_options">Share more options</option>
                                    <option value="negotiate">Negotiate</option>
                                    <option value="drop">Drop</option>
                                </select>
                            </div>

                            <div className="crm-form-group">
                                <label className="crm-label">Notes</label>
                                <textarea
                                    className="crm-textarea"
                                    rows={4}
                                    value={feedbackDraft.notes}
                                    onChange={(event) => setFeedbackDraft((prev) => ({ ...prev, notes: event.target.value }))}
                                    placeholder="Client feedback and next action"
                                />
                            </div>

                            <div className="crm-form-actions">
                                <button
                                    className="crm-btn crm-btn-secondary"
                                    onClick={() => setShowFeedbackModal(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    className="crm-btn crm-btn-primary"
                                    onClick={handleCompleteVisit}
                                >
                                    Save Feedback
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <ConfirmModal
                isOpen={showUnsavedModal}
                onClose={() => {
                    setShowUnsavedModal(false);
                    setPendingTab(null);
                }}
                onConfirm={async () => {
                    const didSave = await requirementsFormRef.current?.save?.();
                    if (!didSave) return;
                    setShowUnsavedModal(false);
                    if (pendingTab) {
                        setActiveTab(pendingTab);
                    }
                    setPendingTab(null);
                }}
                onCancel={() => {
                    setShowUnsavedModal(false);
                    if (pendingTab) {
                        setActiveTab(pendingTab);
                    }
                    setPendingTab(null);
                }}
                title="Unsaved Changes"
                message="You have unsaved changes in Requirements. Save before leaving?"
                confirmText="Save & Leave"
                cancelText="Leave Without Saving"
            />
            <ConfirmDialog />
        </div>
    );
};

export default LeadDetailPage;
